import { memo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Product, ProductCategory } from "@/types/product";
import { Edit, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";

interface ProductCardProps {
  product: Product;
  category?: ProductCategory;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onClick: (product: Product) => void;
  getCategoryName: (categoryId: string) => string;
  getCategoryColor: (categoryId: string) => string;
}

function ProductCardContent({
  product,
  category,
  onEdit,
  onDelete,
  onClick,
  getCategoryName,
  getCategoryColor,
}: ProductCardProps) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { company, can } = useAuth();

  const getCurrencySymbol = () => {
    if ((company as any)?.currency) return (company as any).currency;

    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {
      console.error("Error reading currency from storage", e);
    }
    return "$";
  };

  const currencySymbol = getCurrencySymbol();

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(product.id);
    setIsDeleteOpen(false);
  };

  return (
    <>
      <Card
        className="hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer rounded-[24px] border border-slate-100 bg-white group relative"
        onClick={() => onClick(product)}
      >
        <CardContent className="p-0">
          <div className="bg-gradient-to-r from-slate-50 to-white p-5 border-b border-slate-100 flex items-start justify-between gap-3 rounded-t-[24px]">
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-800 text-base line-clamp-1 group-hover:text-indigo-600 transition-colors">
                  {product.name}
                </h3>
                <Badge
                  style={{
                    backgroundColor: getCategoryColor(product.categoryId) + "15",
                    color: getCategoryColor(product.categoryId),
                  }}
                  className="text-[10px] font-bold px-2 py-0.5 rounded-md whitespace-nowrap border-0 flex-shrink-0"
                >
                  {getCategoryName(product.categoryId)}
                </Badge>
              </div>
              <p className="text-xs font-medium text-slate-400 truncate">{product.sku}</p>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed h-10">
              {product.description || "Sin descripción"}
            </p>

            <div className="flex items-center justify-between pt-4 border-t border-slate-50">
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Precio</span>
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center text-slate-900 font-black text-lg">
                    {currencySymbol}
                    {product.unitPrice.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <span className="text-xs font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">
                    / {product.unit}
                  </span>
                </div>
              </div>

              <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {can("products.update") && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 bg-slate-50 hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 rounded-lg"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEdit(product);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
                {can("products.delete") && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 bg-slate-50 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-lg"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteClick(e);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              el producto "{product.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const ProductCard = memo(ProductCardContent);
